import React, {useEffect} from 'react'
import {Box, Button, Flex, Grid, Link, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import ProductRegistrationHeader from './partials/product-registration-header'
import ProductRegistrationStepper from './partials/product-registration-stepper'
import ProductRegistrationProducts from './partials/product-registration-products'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import {useLocation} from 'react-router-dom'
import routingObject from '../../utils/static-routes'
import {useHistory} from 'react-router-dom/cjs/react-router-dom.min'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

const ProductRegistrationSelect = () => {
    const styles = useMultiStyleConfig('ProductRegistration', {variant: 'productSearch'})

    const location = useLocation()
    const navigate = useNavigation()
    const history = useHistory()
    const {site, locale} = useMultiSite()
    const {currency} = useCurrency()

    const queryParams = new URLSearchParams(location.search)
    const textParam = queryParams.get('text')
    const snParam = queryParams.get('sn')

    useEffect(() => {
        if (snParam && textParam) {
            localStorage.setItem('prepopulatedSerialNumber', snParam)
            queryParams.delete('sn')
            history.replace({search: queryParams.toString()})
        }
    }, [snParam])
    const {data: productSearchResult, isFetching} = useProductSearch(
        {
            parameters: {
                q: textParam || snParam,
                perPricebook: true,
                allVariationProperties: true,
                allImages: true,
                expand: [
                    'promotions',
                    'variations',
                    'prices',
                    'images',
                    'custom_properties',
                    'represented_products',
                ],
                refine: ['c_enabledForReg=true'],
                locale: locale.id,
                site: site.id,
                currency,
                c_isProductReg: true,
            },
        },
        {
            enabled: !!(textParam || snParam),
            keepPreviousData: true,
        },
    )

    const variant =
        productSearchResult?.hits?.length > 2
            ? 'default'
            : productSearchResult?.hits?.length === 2
            ? 'doubleProducts'
            : 'singleProduct'
    const productsVariant = useMultiStyleConfig('ProductRegistrationProducts', {variant})

    const handleButtonClick = (product) => {
        localStorage.setItem('selectedProduct', JSON.stringify(product))
        const productId = product?.representedProduct?.id
        navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/user-detail?code=${productId}`)
    }

    const redirectionPath = `${routingObject.PRODUCT_REGISTRATION_PATH}/search`

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            <Flex {...styles.headerStepperContainer}>
                <ProductRegistrationHeader index={2} variant={'product'} />
                <ProductRegistrationStepper index={1} />
            </Flex>
            {isFetching ? (
                <LoadingSpinner />
            ) : (
                <>
                    {productSearchResult?.hits?.length > 0 ? (
                        <>
                            <Flex {...styles.labelSearchInnerContainer}>
                                <Text {...styles.labelSearchTitle}>
                                    <FormattedMessage
                                        id="product_registration.product_select"
                                        defaultMessage="Select your product"
                                    />
                                </Text>
                            </Flex>
                            <Stack width={'100%'}>
                                <Box width={'100%'}>
                                    <Grid {...productsVariant.productdGrid}>
                                        {productSearchResult.hits?.map((product) => {
                                            return (
                                                <ProductRegistrationProducts
                                                    key={product.productId}
                                                    product={product}
                                                    variant={variant}
                                                    onClick={() => handleButtonClick(product)}
                                                />
                                            )
                                        })}
                                    </Grid>
                                </Box>
                            </Stack>
                        </>
                    ) : (
                        <>
                            <Flex {...styles.productNotFoundInnerContainer}>
                                <Text {...styles.labelSearchTitle}>
                                    <FormattedMessage
                                        id="product_registration.product_not_found.title"
                                        defaultMessage="No products found"
                                    />
                                </Text>
                                <Text {...styles.subtitle}>
                                    <FormattedMessage
                                        id="product_registration.product_not_found.description"
                                        defaultMessage="Please go back and try retyping the SKU or Model Number."
                                    />
                                </Text>
                                <Button
                                    {...styles.tryAgainButton}
                                    variant="primary"
                                    as={Link}
                                    href={redirectionPath}
                                >
                                    <FormattedMessage
                                        defaultMessage="Try again"
                                        id="product_registration.product_not_found.try_again_button"
                                    />
                                </Button>
                            </Flex>
                        </>
                    )}
                </>
            )}
        </Flex>
    )
}

export default ProductRegistrationSelect
